/* The generated line drawings.

   Two so far — growth rings for the hero, and the nested scope forms for the
   "we size the work to the problem" section. They share the wobble and the
   path builder deliberately: the drawings have to read as the same hand, and
   that only holds if the irregularity comes from the same place.

   ---------------------------------------------------------------------------
   Growth rings for the hero drawing.

   Generated rather than hand-drawn, because the whole point is irregularity —
   forty hand-authored bezier curves would end up more even than real growth, and
   even is exactly what made the previous drawing read as a diagram.

   Everything here is deterministic. No Math.random: the drawing has to be the
   same on every render, and a shape that flickers between reloads would read as
   a glitch rather than as a mark.

   What makes it read as organic rather than as concentric circles:
   - gaps between rings vary the way growth does, wide years and lean ones
   - the centre wanders outward, so the rings are never truly concentric
   - each ring's radius wobbles with angle, by a different amount per ring */

// three sine terms of different frequency sum to something that never visibly
// repeats around a circle, which is what an actual ring outline looks like
const wobble = (ring, angle, count) => {

    /* Damped outward. The wobble is a fraction of the radius, so an amplitude
       that looks alive on a 30px ring is ±45px on a 350px one — wider than the
       gap to its neighbour, and the rings cross. Crossings read as noise, not
       as growth, so the outer rings are calmed deliberately. */
    const damp = 1 - (ring / count) * 0.62;

    return damp * (
        Math.sin(angle * 3 + ring * 1.7) * 0.048 +
        Math.sin(angle * 5 + ring * 0.9) * 0.026 +
        Math.sin(angle * 2 + ring * 2.6) * 0.058
    );
}

// wide years and lean years, so the spacing is never a rhythm
const GAPS = [15, 23, 12, 27, 17, 31, 14, 25, 35, 18, 29, 41, 21, 37, 26];

const round = value => Math.round(value * 10) / 10;

/* A deterministic 0–1 per index, used to scatter the animation phases. Stepping
   the phase by index made the weight sweep through a drawing in a clean wave,
   which read as a mechanism rather than as something alive.

   The golden ratio, not a hash. The obvious sin/fract trick distributes badly
   over a handful of small integers — it put nine of the hero's thirteen rings
   inside the same 40% of the cycle, so most of the drawing swelled and thinned
   together and the whole thing read as one slow pulse rather than as lines
   moving independently. Successive multiples of the golden ratio are the classic
   low-discrepancy sequence: neighbouring indices land far apart, and any number
   of them covers the cycle evenly.

   Not Math.random: the phases have to be identical on every render, or a drawing
   would reshuffle its motion on each reload. */
const GOLDEN = 0.618033988749895;

export const scatter = index => {
    const value = (index + 1) * GOLDEN;
    return value - Math.floor(value);
}

/* Catmull-Rom through the sampled points, converted to cubic beziers, so the
   outline is smooth and closed rather than a polygon with visible facets. */
const closedPath = points => {

    const total = points.length;
    let d = `M ${round(points[0][0])} ${round(points[0][1])}`;

    for(let i = 0; i < total; i++) {

        const p0 = points[(i - 1 + total) % total];
        const p1 = points[i];
        const p2 = points[(i + 1) % total];
        const p3 = points[(i + 2) % total];

        const c1 = [p1[0] + (p2[0] - p0[0]) / 6, p1[1] + (p2[1] - p0[1]) / 6];
        const c2 = [p2[0] - (p3[0] - p1[0]) / 6, p2[1] - (p3[1] - p1[1]) / 6];

        d += ` C ${round(c1[0])} ${round(c1[1])}, ${round(c2[0])} ${round(c2[1])}, ${round(p2[0])} ${round(p2[1])}`;
    }
    return `${d} Z`;
}

/* Catmull-Rom through the points as an open path — the closed version above
   wraps around, which is wrong for anything that runs off the edge of a frame. */
const openPath = points => {

    let d = `M ${round(points[0][0])} ${round(points[0][1])}`;

    for(let i = 0; i < points.length - 1; i++) {

        const p0 = points[Math.max(0, i - 1)];
        const p1 = points[i];
        const p2 = points[i + 1];
        const p3 = points[Math.min(points.length - 1, i + 2)];

        const c1 = [p1[0] + (p2[0] - p0[0]) / 6, p1[1] + (p2[1] - p0[1]) / 6];
        const c2 = [p2[0] - (p3[0] - p1[0]) / 6, p2[1] - (p3[1] - p1[1]) / 6];

        d += ` C ${round(c1[0])} ${round(c1[1])}, ${round(c2[0])} ${round(c2[1])}, ${round(p2[0])} ${round(p2[1])}`;
    }
    return d;
}

/* ---------------------------------------------------------------------------
   Contours, for the margins of the Quick Look band.

   Strata rather than rings: open lines that run off both ends of the frame,
   packed tight at the outer edge and opening out as they move inward. It reads
   as ground being surveyed, which is what the Quick Look does, and it is a
   different motif from the hero — the rings are closed and nested, these are
   open and roughly parallel, so the two never look like the same drawing used
   twice.

   Most of the wander is shared between the lines and only a little of it is per
   line. That is what keeps them from crossing: contours that cross stop reading
   as terrain. The per-line term is capped well under the tightest gap below. */

const CONTOUR_GAPS = [14, 22, 16, 29, 19, 34, 24, 41, 27, 36];

export const contourField = ({ from = 'left', height = 430, lines = 9, samples = 16, seed = 0, width = 300 } = {}) => {

    const field = [];
    let offset = 12;

    for(let line = 0; line < lines; line++) {

        const points = [];
        for(let sample = 0; sample <= samples; sample++) {

            const y = -24 + (sample / samples) * (height + 48);
            const drift =
                Math.sin(y * 0.0075 + seed) * 16 +
                Math.sin(y * 0.019 + seed * 1.3) * 7 +
                Math.sin(y * 0.013 + line * 2.1) * 4;

            points.push([from === 'left' ? offset + drift : width - offset + drift, y]);
        }

        field.push({ d: openPath(points), line: line, phase: scatter(line + seed * 11 + 200) });
        offset += CONTOUR_GAPS[line % CONTOUR_GAPS.length];
    }

    return field;
}

export const growthRings = ({ centre = [318, 222], count = 13, samples = 44, squash = 0.84, start = 26 } = {}) => {

    const rings = [];
    let radius = start;

    for(let ring = 0; ring < count; ring++) {

        // the pith wanders as the trunk grows, so no two rings share a centre —
        // but only just. Push this further and the outer rings stop reading as
        // growth around the same core and start looking misaligned.
        const cx = centre[0] + ring * 0.5 + Math.sin(ring * 0.7) * 3;
        const cy = centre[1] - ring * 0.35 + Math.cos(ring * 0.55) * 2.5;

        const points = [];
        for(let step = 0; step < samples; step++) {
            const angle = (step / samples) * Math.PI * 2;
            const r = radius * (1 + wobble(ring, angle, count));
            points.push([cx + Math.cos(angle) * r, cy + Math.sin(angle) * r * squash]);
        }

        rings.push({ d: closedPath(points), phase: scatter(ring), radius: radius, ring: ring });

        // gaps widen slightly outward so the damped wobble still has room
        radius += GAPS[ring % GAPS.length] + ring * 1.6;
    }

    return rings;
}

/* ---------------------------------------------------------------------------
   Scope forms, for "we size the work to the problem".

   Five closed forms, each containing the next, but nested hard off-centre
   rather than concentrically. Two things fall out of that, and both are the
   section's argument:

   - the sizes are wildly different, which is what "sized to the problem" looks
     like when you cannot use words for it
   - the offsets accumulate in one direction, so the whole thing reads as having
     grown outward from the smallest form rather than as a target with a bullseye

   The smallest is the accent: the single fix the largest relationships started
   as. Nothing here is labelled, and nothing is a diagram — same rule as the
   hero. Radii and offsets are held below rather than computed, because the
   containment constraint (offset <= outer − inner) is what stops a form from
   breaking out of its parent, and that is easier to see when the numbers sit
   next to each other. */

const FORMS = [
    { radius: 26,  step: 0 },
    { radius: 42,  step: 10 },
    { radius: 68,  step: 16 },
    { radius: 108, step: 24 },
    { radius: 172, step: 38 },
    { radius: 268, step: 58 }
];

/* How far a form may wobble, as a fraction of its radius. The ceiling is set by
   the tightest clearance in FORMS above — (outer − inner) − step — which is 6px
   on a 42px form. Anything looser and a form crosses the one holding it, which
   stops reading as growth and starts reading as a mistake. */
const FORM_WOBBLE = 0.62;

// up and to the right, so the growth has a direction
const DRIFT = [0.78, -0.62];

export const scopeForms = ({ origin = [188, 322], samples = 46, squash = 0.86 } = {}) => {

    let cx = origin[0];
    let cy = origin[1];

    return FORMS.map((form, index) => {

        cx += DRIFT[0] * form.step;
        cy += DRIFT[1] * form.step;

        const points = [];
        for(let sample = 0; sample < samples; sample++) {
            const angle = (sample / samples) * Math.PI * 2;
            // offset the wobble phase per form so no two outlines rhyme
            const r = form.radius * (1 + wobble(index * 3 + 2, angle, 60) * FORM_WOBBLE);
            points.push([cx + Math.cos(angle) * r, cy + Math.sin(angle) * r * squash]);
        }

        return { d: closedPath(points), form: index, phase: scatter(index + 40), radius: form.radius };
    });
}

/* ---------------------------------------------------------------------------
   Process steps, for the How We Work hero.

   Four forms along a rising line, each larger than the last, joined by short
   connectors that stop at the edges rather than running through. The forms are
   what carry the page's argument: the steps are separate things, they grow, and
   the line between them ends at the last one rather than arrowing off — nothing
   here funnels you anywhere.

   The first form is the accent: the free Quick Look, where the page says most
   people start and plenty of people stop.

   Same wobble and path builder as the other two drawings, so it reads as the
   same hand. A connector is a curve, not a straight rule — a straight line
   between two hand-drawn shapes reads as a diagram immediately. */

const STEPS = [
    { at: [70, 252],  radius: 18 },
    { at: [190, 226], radius: 30 },
    { at: [330, 194], radius: 48 },
    { at: [492, 150], radius: 76 }
];

// how far a connector stops short of each form it joins
const CLEARANCE = 9;

export const processSteps = ({ samples = 40, squash = 0.86 } = {}) => {

    const forms = STEPS.map((step, index) => {

        const points = [];
        for(let sample = 0; sample < samples; sample++) {
            const angle = (sample / samples) * Math.PI * 2;
            const r = step.radius * (1 + wobble(index * 4 + 3, angle, 70) * 0.7);
            points.push([
                step.at[0] + Math.cos(angle) * r,
                step.at[1] + Math.sin(angle) * r * squash
            ]);
        }
        return { d: closedPath(points), phase: scatter(index + 80), step: index };
    });

    const links = STEPS.slice(0, -1).map((step, index) => {

        const next = STEPS[index + 1];
        const span = [next.at[0] - step.at[0], next.at[1] - step.at[1]];
        const length = Math.sqrt(span[0] * span[0] + span[1] * span[1]);
        const unit = [span[0] / length, span[1] / length];

        const from = [
            step.at[0] + unit[0] * (step.radius + CLEARANCE),
            step.at[1] + unit[1] * (step.radius + CLEARANCE)
        ];
        const to = [
            next.at[0] - unit[0] * (next.radius + CLEARANCE),
            next.at[1] - unit[1] * (next.radius + CLEARANCE)
        ];

        // bow the connector off the straight line, alternating side by side
        const bow = (index % 2 === 0 ? 1 : -1) * 13;
        const mid = [(from[0] + to[0]) / 2 - unit[1] * bow, (from[1] + to[1]) / 2 + unit[0] * bow];

        return {
            d: `M ${round(from[0])} ${round(from[1])} Q ${round(mid[0])} ${round(mid[1])}, ${round(to[0])} ${round(to[1])}`,
            link: index,
            phase: scatter(index + 120)
        };
    });

    return { forms: forms, links: links };
}

/* ---------------------------------------------------------------------------
   Arrangements, for the Ways to Work hero.

   Three forms set around a shared one: the three ways the work can be structured,
   all bound to the same thing — a solution that improves the business. That is
   the page's argument, and it is the one part of it that does not depend on which
   arrangement you pick.

   The shared form is drawn as three arcs rather than a full circle, each running
   between the two nodes either side of it and stopping where they begin. Drawing
   it whole would put a line straight through all three nodes; crossings read as
   noise rather than as structure.

   Two things keep the joins honest:

   - each arc's wobble tapers to nothing at both ends, so its endpoints land
     exactly on the ideal circle the stopping angle was computed from. Wobbling
     endpoints wandered inside the nodes and the arcs appeared to run over them.
   - the stopping angle is computed against a node slightly larger than the one
     drawn, so an arc always ends outside the node's wobble rather than somewhere
     within it. The result is a hairline of clearance, the same way the connectors
     stop short of the forms in the process drawing.
   ========================================================================== */

const HUB_RADIUS = 150;
const NODE_DISTANCE = 128;

/* Three peers, not three sizes — but drawn by hand nothing is identical, so the
   radii differ by a few percent. Enough to stop them reading as one shape
   stamped three times, not enough to imply one matters more. */
const NODE_RADII = [64, 59, 67];

/* How far the outlines wander, as a fraction of radius. These are high on
   purpose: the wobble is what makes the drawing look drawn rather than like a
   stock trefoil, and the endpoint taper below means the joins survive it. */
const ARC_WANDER = 0.62;
const NODE_WANDER = 0.72;

// the widest a wobble can push an outline out from its nominal radius
const WOBBLE_PEAK = 0.048 + 0.026 + 0.058;

// evenly around the circle, starting at the top
const NODE_ANGLES = [-Math.PI / 2, Math.PI / 6, (5 * Math.PI) / 6];

// the hub's centre, so every coordinate lands positive
const ORIGIN = [190, 190];

/* The arcs do not animate at all — the shared form is the one thing on the page
   that does not change, and a line that swells and thins says the opposite. The
   nodes each get their own phase; see NODE_MOTION in views/DocPage.js, which
   also gives each one its own period and its own range to move through. */

/* An open arc, wobbled in the middle and true at both ends. The taper is what
   makes the endpoints land exactly where the geometry says they should. */
const arcPath = (radius, from, to, seed, samples) => {

    const points = [];
    for(let sample = 0; sample <= samples; sample++) {

        const along = sample / samples;
        const angle = from + along * (to - from);
        const taper = Math.sin(Math.PI * along);
        const r = radius * (1 + wobble(seed, angle, 90) * ARC_WANDER * taper);

        points.push([ORIGIN[0] + Math.cos(angle) * r, ORIGIN[1] + Math.sin(angle) * r]);
    }
    return openPath(points);
}

export const arrangementCluster = ({ samples = 30 } = {}) => {

    /* Half the angle a node cuts out of the hub, from the triangle formed by the
       hub centre, the node centre and the point where the two circles meet.

       Each node is treated as larger than it is drawn by its own wobble peak
       plus a hair, so an arc always stops outside the node's outermost excursion
       rather than somewhere inside it. Derived from the wander above rather than
       hard-coded, so raising the wander cannot silently reopen the joins. */
    const cuts = NODE_RADII.map(radius => {
        const clearance = radius * (1 + WOBBLE_PEAK * NODE_WANDER) + 4;
        const cosine = (HUB_RADIUS * HUB_RADIUS + NODE_DISTANCE * NODE_DISTANCE - clearance * clearance)
            / (2 * HUB_RADIUS * NODE_DISTANCE);
        return Math.acos(Math.max(-1, Math.min(1, cosine)));
    });

    const hub = NODE_ANGLES.map((angle, index) => {
        const after = (index + 1) % NODE_ANGLES.length;
        const next = index === NODE_ANGLES.length - 1
            ? NODE_ANGLES[0] + Math.PI * 2
            : NODE_ANGLES[index + 1];
        return {
            arc: index,
            d: arcPath(HUB_RADIUS, angle + cuts[index], next - cuts[after], index * 3 + 1, samples)
        };
    });

    const nodes = NODE_ANGLES.map((angle, index) => {

        const cx = ORIGIN[0] + Math.cos(angle) * NODE_DISTANCE;
        const cy = ORIGIN[1] + Math.sin(angle) * NODE_DISTANCE;

        const points = [];
        for(let sample = 0; sample < samples * 2; sample++) {
            const step = (sample / (samples * 2)) * Math.PI * 2;
            const r = NODE_RADII[index] * (1 + wobble(index * 7 + 5, step, 80) * NODE_WANDER);
            points.push([cx + Math.cos(step) * r, cy + Math.sin(step) * r]);
        }

        return { d: closedPath(points), node: index, phase: scatter(index + 230) };
    });

    return { hub: hub, nodes: nodes };
}

/* ---------------------------------------------------------------------------
   One long line, for the articles hero.

   A single stroke that runs the width, turns, and comes back — six passes, one
   continuous line, with a small mark resting on it.

   Two earlier attempts here failed for the same underlying reason, which is
   worth keeping. Rounded rectangles forced the wobble down to protect their
   corners, and low wobble is what makes a drawing look mechanical. Separate
   horizontal strokes kept the wobble but had nothing joining them, so they read
   as scattered rules rather than as one thing. Continuity is what was missing:
   the turns at the ends are what make six lines into one, and one line is what a
   body of writing actually is. */

/* Each pass has its own length, its own gap to the next one, and its own turn.
   That is the whole difference between this and a machine-drawn serpentine —
   noise on top of an even structure still reads as even, because the eye reads
   the structure first. Ragged ends and uneven gaps are what make it a hand. */
const PASSES = [
    { bulge: 20, gap: 30, left: 66, right: 352 },
    { bulge: 15, gap: 35, left: 80, right: 336 },
    { bulge: 23, gap: 27, left: 56, right: 361 },
    { bulge: 17, gap: 36, left: 86, right: 342 },
    { bulge: 21, gap: 29, left: 61, right: 355 },
    { bulge: 0,  gap: 0,  left: 74, right: 338 }
];

const PASS_TOP = 92;

// how far the line wanders off its course, in user units
const LINE_WANDER = 5;

export const thoughtLine = ({ samples = 30 } = {}) => {

    const points = [];
    let y = PASS_TOP;

    PASSES.forEach((pass, index) => {

        const rightwards = index % 2 === 0;
        const from = rightwards ? pass.left : pass.right;
        const to = rightwards ? pass.right : pass.left;

        // the pass itself, bowing gently and alternating which way it bows
        for(let sample = 0; sample <= samples; sample++) {
            const along = sample / samples;
            const sag = (rightwards ? -1 : 1) * (4 + (index % 3) * 1.6) * Math.sin(Math.PI * along);
            points.push([from + along * (to - from), y + sag]);
        }

        if(index === PASSES.length - 1) {
            return;
        }

        /* The turn out to the next pass. Its ends are at different x values now
           that the passes are ragged, so it is sampled as a loop that bulges past
           whichever is further out and lands on the next start — the smoothing in
           openPath carries the join. */
        const next = PASSES[index + 1];
        const nextFrom = rightwards ? next.right : next.left;
        const edge = rightwards ? Math.max(to, nextFrom) : Math.min(to, nextFrom);

        for(let step = 1; step < 6; step++) {
            const angle = -Math.PI / 2 + (step / 6) * Math.PI;
            points.push([
                edge + (rightwards ? 1 : -1) * pass.bulge * Math.cos(angle),
                y + (pass.gap / 2) * (1 + Math.sin(angle))
            ]);
        }

        y = y + pass.gap;
    });

    /* One wander pass over the whole stroke, so the turns move with the passes
       rather than sitting on a true vertical. Both axes, at different rates. */
    const wandered = points.map((point, index) => {
        const along = (index / points.length) * Math.PI;
        return [
            point[0] + wobble(5, along * 7, 90) * LINE_WANDER * 0.6,
            point[1] + wobble(2, along * 12, 90) * LINE_WANDER
        ];
    });

    /* A small mark resting on the line — the one accent, and the only closed
       form in the drawing, so the eye has somewhere to land. */
    const centre = wandered[Math.round(wandered.length * 0.46)];
    const mark = [];
    for(let sample = 0; sample < 34; sample++) {
        const angle = (sample / 34) * Math.PI * 2;
        const r = 13 * (1 + wobble(9, angle, 70) * 0.6);
        mark.push([centre[0] + Math.cos(angle) * r, centre[1] + Math.sin(angle) * r * 0.88]);
    }

    return { line: openPath(wandered), mark: closedPath(mark) };
}
